/**
 * @fileoverview The plants taken out of the garden (GET /plants?status=0), one
 * read for every screen that needs them: the plants grid's archived filter, the
 * Archived plants page and the season review's share sheet (while it is
 * open: its removed plants' photos). Beds, Seasons and Season review no longer
 * read it when they open (W24 Job H): a tapped row's plant is read by its id
 * (shared/hooks/usePlantById). They all share one query, so one request and one
 * cache entry, and every place that archives or restores a plant refreshes it
 * by the same key (ARCHIVED_PLANTS_QUERY_KEY).
 *
 * Where: PlantGrid, ArchivedPlants, SeasonReviewPage (the share sheet).
 * API: GET /plants?status=0 (getArchivedPlants).
 * Settings: none; the query key is this hook's own (the same word the invalidations in PlantsContext, ArchiveOrDeleteDialog and the grids use).
 *
 * @module useArchivedPlants
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { getArchivedPlants } from '@/shared/api/plants';
import type { Plant } from '@/shared/types/plant';

/** The query key the archived plants are kept under; a writer that archives or restores a plant invalidates it. */
export const ARCHIVED_PLANTS_KEY = 'archived-plants';

/**
 * The gardener's archived plants, read once for the whole app.
 *
 * @param wanted - Whether the screen needs them now (the grid reads them only when its filter shows them); true by default. Nothing is read until the gardener is signed in.
 * @returns The query: `data` is the archived plants (undefined until they arrive).
 */
export function useArchivedPlants(wanted = true): UseQueryResult<Plant[]> {
  const { mysqlUser } = useAuth();
  const userId = mysqlUser?.user_id ?? null;
  return useQuery({
    queryKey: [ARCHIVED_PLANTS_KEY, userId],
    queryFn: () => getArchivedPlants(),
    enabled: userId !== null && wanted,
  });
}
